import React, { RefObject, useCallback } from 'react';

import { Button } from '@vtblife/uikit';
import classname from '@search/classname/src';
import useControl from '@search/hooks/src/useControl';
import {
    Size,
    View,
    Tone,
    SizeEnum,
    ViewEnum,
    ToneEnum,
    Maybe
} from '../../../types';

import { ICheckboxControlProps, CheckboxControl } from './CheckboxControl/CheckboxControl';

import './Checkbox.css';

export type CheckboxControlProps = Omit<ICheckboxControlProps, 'size'>;

export interface ICheckboxProps extends CheckboxControlProps {
    /**
     * Ссылка на корневой DOM-элемент компонента.
     */
    innerRef?: RefObject<HTMLLabelElement>;

    /**
     * Ссылка на DOM элемент нативного инпута.
     */
    controlRef?: RefObject<HTMLInputElement>;

    /**
     * Вид элемента
     */
    view?: View;

    /**
     * Цветовое решение
     */
    tone?: Tone;

    /**
     * Размер контрола
     */
    size?: Size;

    /**
     * Состояние переключателя: включен или выключен
     */
    checked?: boolean;

    /**
     * Тип отображения
     */
    type?: 'button' | 'checkbox';

    square?: boolean;

    onCheck?: (
        checked: boolean,
        props: { name: Maybe<string>; value: Maybe<string | number | readonly string[]>;
    }) => void;

    width?: number | string;

    additionalContent?: React.ReactNode;
}

export const cnCheckbox = classname.bind(null, 'Checkbox2');

const Checkbox = ({
    name,
    type,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    square = false,
    className,
    value = '',
    innerRef,
    checked = false,
    disabled = false,
    autoComplete = 'off',
    size = SizeEnum.S,
    view = ViewEnum.PRIMARY,
    tone = ToneEnum.PRIMARY,
    width,
    additionalContent,
    onCheck,
    ...props
}: ICheckboxProps): React.ReactElement => {
    const [ buttonState, controlHandlers ] = useControl(
        { disabled, focused: false },
        props,
        { disabled }
    );

    const handleChange = useCallback(() => {
        if (disabled) {
            return;
        }

        onCheck?.(! checked, { name, value });
    }, [ name, value, disabled, checked, onCheck ]);

    const handleButtonClick = useCallback(() => {
        onCheck?.(! checked, { name, value });
    }, [ name, value, checked, onCheck ]);

    if (type === 'button') {
        return (
            <label
                ref={innerRef}
                className={cnCheckbox(null, { ...buttonState, checked, tone, size, view, type }, className)}>
                {checked && (
                    <input type='hidden' name={name} value={value} disabled={disabled} />
                )}
                <Button
                    fullWidth
                    type='button'
                    variant='secondary'
                    size='s'
                    disabled={disabled}
                    selected={checked}
                    onClick={handleButtonClick}>
                    {props.children}
                </Button>
                {additionalContent}
            </label>
        );
    }

    return (
        <label
            htmlFor={name}
            ref={innerRef}
            onMouseUp={controlHandlers.handleMouseUp}
            onMouseDown={controlHandlers.handleMouseDown}
            onMouseEnter={controlHandlers.handleMouseEnter}
            onMouseLeave={controlHandlers.handleMouseLeave}
            className={cnCheckbox(null, { ...buttonState, disabled, checked, tone, size, view }, className)}>
            <span className={cnCheckbox('box')}>
                <CheckboxControl
                    id={name}
                    name={name}
                    value={value}
                    controlRef={props.controlRef}
                    checked={checked}
                    disabled={disabled}
                    onChange={handleChange}
                    autoComplete={autoComplete}
                />
                <span className={cnCheckbox('tick')} />
            </span>
            <span
                role='presentation'
                className={cnCheckbox('text')}>
                {props.children}
            </span>
        </label>
    );
};

export default Checkbox;
